import TextField from "@material-ui/core/TextField"
import { SaveAlt as DownloadIcon } from "@material-ui/icons"
import SentimentVeryDissatisfiedIcon from "@material-ui/icons/SentimentVeryDissatisfied"
import { IconArrowNarrowDown, IconArrowNarrowUp } from "@tabler/icons-react"
import { Cell, Column, Plugins, Table } from "fixed-data-table-2"
import "fixed-data-table-2/dist/fixed-data-table.css"
import React from "react"
import Dimensions from "react-dimensions"
import { API_BASE, API_ROOT } from "../../config"
import { withQueryStrings } from "../../utils"
import Box from "./Box"
import DownloadButton from "./DownloadButton"
import FixedHeaderSortableTable from "./FixedHeaderSortableTable"
import HStack from "./HStack"
import WhitePaper from "./WhitePaper"

const DefaultTextCell = ({ rowIndex, data, col, ...props }) => (
  <Cell {...props}>{data[rowIndex][col]}</Cell>
)

const NonDefaultTextCell = ({ data }) => <span>{data}</span>

const IndexCell = ({ rowIndex, data, col, ...props }) => (
  <Cell {...props}>{rowIndex + 1}</Cell>
)

export const SortTypes = {
  ASC: "ASC",
  DESC: "DESC",
}

function reverseSortDirection(sortDir) {
  return sortDir === SortTypes.DESC ? SortTypes.ASC : SortTypes.DESC
}

export class SortHeaderCell extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      isHovered: false,
    }
  }

  render() {
    const { onSortChange, sortDir, children, ...props } = this.props
    const { isHovered } = this.state
    return (
      <HStack
        {...props}
        style={{
          cursor: "pointer",
          backgroundColor: isHovered ? "#d3d3d3" : "transparent",
        }}
        onClick={this._onSortChange}
        onMouseEnter={this._handleMouseEnter}
        onMouseLeave={this._handleMouseLeave}
        justify={"center"}
        align={"center"}
      >
        {children}{" "}
        {sortDir ? (
          sortDir === SortTypes.DESC ? (
            <IconArrowNarrowUp size="16px" />
          ) : (
            <IconArrowNarrowDown size="16px" />
          )
        ) : (
          ""
        )}
      </HStack>
    )
  }

  _onSortChange = (e) => {
    e.preventDefault()

    if (this.props.onSortChange) {
      this.props.onSortChange(
        this.props.columnKey,
        this.props.sortDir
          ? reverseSortDirection(this.props.sortDir)
          : SortTypes.DESC,
      )
    }
  }
  _handleMouseEnter = () => {
    this.setState({ isHovered: true })
  }

  _handleMouseLeave = () => {
    this.setState({ isHovered: false })
  }
}

// TODO Convert this to functional component
class SortableDataTable extends React.Component {
  constructor(props) {
    super(props)
    const { data = [], keys = [] } = props

    this.state = {
      sortedDataList: data,
      sortKey: null,
      sortOrder: SortTypes.ASC,
      query: "",
      downloadInProgress: false,
      hasEncounteredError: false,
      columnWidths: keys?.reduce((acc, key) => {
        // Remove any trailing tabs or spaces
        const cleanKey = key.trim()
        acc[cleanKey] = 150
        return acc
      }, {}),
    }
  }

  initializeDataList = (
    data,
    sortKey = this.state.sortKey,
    sortOrder = this.state.sortOrder,
  ) => {
    this.setState({
      sortedDataList: this.filteredData(
        this.sortedData(data, sortKey, sortOrder),
      ),
      sortKey,
      sortOrder,
    })
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { data, needUpdate } = nextProps
    const { sortKey, sortOrder } = this.state
    this.initializeDataList(data, sortKey, sortOrder)
    if (needUpdate) {
      //FIXME: Will be called several times
      this.initializeDataList(data)
    }
  }

  sortedData = (data, sortKey, sortOrder) => {
    const normalizeString = (value) => {
      // If value is not a string, convert to string
      if (typeof value !== "string") {
        value = String(value)
      }

      // Trim and convert to lowercase
      return (
        value
          .trim()
          // Replace underscores and hyphens with spaces
          .replace(/[_-]/g, " ")
          // Remove special characters
          .replace(/[^a-z0-9 ]/gi, "")
          // Convert camelCase to space-separated
          .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
          // Convert to lowercase
          .toLowerCase()
      )
    }

    if (!sortKey || !sortOrder) {
      return data.slice()
    }

    // Enhanced default comparator with normalization
    const defaultComparator = (valA, valB, isASC) => {
      // Handle null, undefined, and empty string cases
      const valueA = valA ?? ""
      const valueB = valB ?? ""

      // Normalize to handle different string formats
      const strA = normalizeString(valueA)
      const strB = normalizeString(valueB)

      // Special handling for numeric sorting
      const numA = isNaN(valueA) ? strA : Number(valueA)
      const numB = isNaN(valueB) ? strB : Number(valueB)

      // Prioritize numeric comparison if both are numeric
      if (!isNaN(numA) && !isNaN(numB)) {
        return isASC ? numA - numB : numB - numA
      }

      // Fallback to normalized string comparison
      if (strA < strB) return isASC ? -1 : 1
      if (strA > strB) return isASC ? 1 : -1
      return 0
    }

    // Use provided comparators or default to the enhanced comparator
    const { comparators = {} } = this.props || {}
    const comparator = comparators[sortKey] || defaultComparator

    // Create a copy and sort
    return data.slice().sort((itemA, itemB) => {
      // Safely convert sort key to string and get values
      const sortKeyStr = String(sortKey)
      const valueA = itemA[sortKeyStr]
      const valueB = itemB[sortKeyStr]

      // Use the comparator with ASC/DESC flag
      return comparator(valueA, valueB, sortOrder === SortTypes.ASC)
    })
  }

  filteredData = (data, query = this.state.query) => {
    return data.filter((o) => {
      return Object.values(o).some(
        (term) => String(term).toLowerCase().indexOf(query.toLowerCase()) > -1,
      )
    })
  }

  _onSortChange = (sortKey, sortOrder) => {
    this.setState({
      sortedDataList: this.sortedData(
        this.state.sortedDataList,
        sortKey,
        sortOrder,
      ),
      sortKey,
      sortOrder,
    })
  }

  _onColumnResizeEndCallback = (newColumnWidth, columnKey) => {
    this.setState(({ columnWidths }) => ({
      columnWidths: {
        ...columnWidths,
        [columnKey]: newColumnWidth,
      },
    }))
  }

  render() {
    const { sortedDataList, sortKey, sortOrder, query, columnWidths } =
      this.state
    const {
      data = [],
      headerLabels = [],
      keys = [],
      containerWidth,
      defaultTable = true,
      TextCell = defaultTable ? DefaultTextCell : NonDefaultTextCell,
      withoutBox = false,
      boxProps = {},
      withoutSerialNumbers = false,
      tableType,
      tableHeight = null,
    } = this.props

    let rowHeight = 0,
      height = 0,
      length = 0
    if (sortedDataList) {
      length = sortedDataList.length
      rowHeight = 50
      height = rowHeight * (length > 10 ? 10 : length + 1)
    }
    if (withoutBox) {
      return (
        <Table
          {...this.props}
          containerHeight={
            this.props.containerHeight > 0 ? this.props.containerHeight : 500
          }
          rowHeight={rowHeight}
          rowsCount={length}
          headerHeight={rowHeight}
          width={containerWidth - 16}
          height={height}
        >
          {!withoutSerialNumbers && (
            <Column
              header={<Cell>Sno.</Cell>}
              cell={
                <IndexCell
                  data={sortedDataList}
                  col="sno"
                />
              }
              fixed={true}
              width={50}
            />
          )}
          {keys.map((key, index) => {
            return (
              <Column
                key={key}
                columnKey={key}
                header={
                  <SortHeaderCell
                    onSortChange={this._onSortChange}
                    sortDir={key === sortKey ? sortOrder : null}
                  >
                    {headerLabels[index]}
                  </SortHeaderCell>
                }
                cell={
                  <TextCell
                    data={sortedDataList}
                    col={key}
                  />
                }
                width={120}
                flexGrow={1}
              />
            )
          })}
        </Table>
      )
    } else {
      const {
        withoutSearch = false,
        withoutDownload = false,
        downloadReportTitle,
        downloadParams: {
          appId,
          filters,
          queryParams,
          dimensions,
          auth: {
            user: { email },
          },
          isBiReport = false,
        },
      } = this.props

      return (
        <Box
          {...boxProps}
          controls={
            <div style={{ display: "flex", overflow: "hidden" }}>
              {!withoutSearch && (
                <TextField
                  value={query}
                  onChange={(e) => {
                    const query = e.target.value
                    this.setState({
                      query,
                      sortedDataList: this.sortedData(
                        this.filteredData(data, query),
                      ),
                    })
                  }}
                  placeholder="Search..."
                  type="search"
                />
              )}
              {!withoutDownload && (
                <DownloadButton
                  url={
                    `${API_BASE}${isBiReport ? "bi/frontendapi/derived/" : API_ROOT}custom-reports/${tableType}?customerId=${email}&appId=${appId}` +
                    withQueryStrings({
                      ...queryParams,
                      download: true,
                    })
                  }
                  method={"POST"}
                  data={{
                    metrics: ["impressions", "sessions", "users"],
                    dimensions: dimensions,
                    filters: filters,
                  }}
                  downloadReportTitle={downloadReportTitle}
                >
                  <DownloadIcon
                    color="primary"
                    fontSize="small"
                  />
                </DownloadButton>
              )}
            </div>
          }
          component={WhitePaper}
          styles={{ border: "none" }}
        >
          {sortedDataList.length === 0 && (
            <HStack
              style={{
                width: "100%",
                height: "50vh",
                border: "1px solid #d3d3d3",
              }}
              spacing={4}
              align={"center"}
              justify={"center"}
            >
              <SentimentVeryDissatisfiedIcon color="error" />
              No Data.
            </HStack>
          )}
          {sortedDataList.length !== 0 && !defaultTable && (
            <FixedHeaderSortableTable
              keys={keys}
              rows={sortedDataList}
              headerLabels={headerLabels}
              TextCell={TextCell}
            />
          )}
          {sortedDataList.length !== 0 && defaultTable && (
            <Table
              {...this.props}
              rowHeight={rowHeight}
              rowsCount={length}
              headerHeight={rowHeight}
              width={containerWidth - 16}
              height={tableHeight ? tableHeight : height}
            >
              {!withoutSerialNumbers && (
                <Column
                  header={<Cell>Sno.</Cell>}
                  cell={
                    <IndexCell
                      data={sortedDataList}
                      col="sno"
                    />
                  }
                  fixed={true}
                  width={50}
                />
              )}
              {keys.map((key, index) => {
                return (
                  <Column
                    key={key}
                    columnKey={key}
                    header={
                      <Plugins.ResizeCell
                        onColumnResizeEnd={this._onColumnResizeEndCallback}
                      >
                        <SortHeaderCell
                          onSortChange={this._onSortChange}
                          sortDir={key === sortKey ? sortOrder : null}
                        >
                          {headerLabels[index]}
                        </SortHeaderCell>
                      </Plugins.ResizeCell>
                    }
                    cell={
                      <TextCell
                        data={sortedDataList}
                        col={key}
                      />
                    }
                    width={columnWidths[key] ?? 150}
                    flexGrow={1}
                  />
                )
              })}
            </Table>
          )}
        </Box>
      )
    }
  }
}

export default Dimensions()(SortableDataTable)
